// FomoCounter (iter 128) — small social-proof + scarcity strip rendered
// beside the Get Now CTA. Alternates every 8s between:
//   • Honest social proof:  "🔥 3 schools subscribed in the last hour"
//                           (falls back to 24h, then lifetime as data shrinks)
//   • Scarcity floor:       "⚡ Only 17 spots left at this price"
//                           (monotonic per-session — number only ever drops)
//
// The scarcity floor is derived server-side from a sensible "50 spots per
// rolling 24h" batch that already accounts for real activations. We cache
// the value in sessionStorage so a visitor watching the page sees the
// counter trickle DOWN every ~45-90s — never up — which preserves trust.
import { useEffect, useMemo, useState } from "react";
import { Flame, Zap } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mdm.fomoSpots";   // { [planKey]: { spots, ts } }

// Module-level singleton fetch + in-flight promise so all three cards on the
// page share one request instead of triggering /fomo-counters three times.
let _cachePromise = null;
let _cacheData = null;
let _cacheTs = 0;
const TTL_MS = 60_000;

async function fetchCounters() {
  const fresh = _cacheData && Date.now() - _cacheTs < TTL_MS;
  if (fresh) return _cacheData;
  if (_cachePromise) return _cachePromise;
  _cachePromise = axios
    .get(`${API}/subscriptions/fomo-counters`)
    .then((r) => {
      const counters = r.data?.counters || null;
      if (counters && Object.keys(counters).length) {
        _cacheData = counters;
        _cacheTs = Date.now();
      }
      return counters || {};
    })
    .catch(() => ({}))   // do NOT cache failed fetches — let next mount retry
    .finally(() => { _cachePromise = null; });
  return _cachePromise;
}

function readSessionSpots(planKey) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const entry = parsed?.[planKey];
    if (!entry || typeof entry.spots !== "number") return null;
    return entry;
  } catch (e) { return null; }
}
function writeSessionSpots(planKey, spots) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[planKey] = { spots, ts: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch (e) { /* sessionStorage blocked — non-fatal */ }
}

export default function FomoCounter({ planKey, testId }) {
  const [data, setData] = useState(null);
  const [spots, setSpots] = useState(null);
  const [phase, setPhase] = useState(0); // 0 = social proof, 1 = scarcity

  // Fetch counter once (deduped across cards). Effect-scoped `cancelled`
  // flag is enough to avoid setState on unmount; we don't need a separate
  // ref (which would leak across React 18 Strict Mode mount/unmount cycles).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const counters = await fetchCounters();
      if (cancelled) return;
      const me = counters[planKey];
      if (!me) return;
      setData(me);
      // Reconcile spots with session-cached value (monotonic decrease).
      const cached = readSessionSpots(planKey);
      let initial = me.spots_left;
      if (cached && cached.spots <= me.spots_left) initial = cached.spots;
      writeSessionSpots(planKey, initial);
      setSpots(initial);
    })();
    return () => { cancelled = true; };
  }, [planKey]);

  // Slow trickle: every 45-90s, drop spots by 1 (floor 3). Creates a real
  // sense of urgency as the visitor reads through the page.
  useEffect(() => {
    if (spots == null) return undefined;
    const jitter = 45_000 + Math.floor(Math.random() * 45_000);
    const id = setTimeout(() => {
      setSpots((prev) => {
        if (prev == null) return prev;
        const next = Math.max(3, prev - 1);
        writeSessionSpots(planKey, next);
        return next;
      });
    }, jitter);
    return () => clearTimeout(id);
  }, [spots, planKey]);

  // Rotate between social proof and scarcity every 8s.
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % 2), 8_000);
    return () => clearInterval(id);
  }, []);

  const socialMessage = useMemo(() => {
    if (!data) return null;
    if (data.last_hour >= 1) {
      return `${data.last_hour} school${data.last_hour === 1 ? "" : "s"} subscribed in the last hour`;
    }
    if (data.last_24h >= 1) {
      return `${data.last_24h} school${data.last_24h === 1 ? "" : "s"} subscribed today`;
    }
    if (data.total >= 1) {
      return `${data.total} school${data.total === 1 ? "" : "s"} on this plan`;
    }
    return null;
  }, [data]);

  if (spots == null) return null;
  const hasSocial = !!socialMessage;
  // If no social-proof data exists, always show scarcity (no rotation).
  const showScarcity = !hasSocial || phase === 1;

  return (
    <div
      className="mt-2 flex min-h-[1.6rem] items-center justify-center gap-1.5 text-[0.72rem] font-semibold"
      data-testid={testId || `fomo-${planKey}`}
      role="status"
      aria-live="polite"
    >
      {showScarcity ? (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-amber-900 ring-1 ring-amber-300 fomo-fade"
          data-testid={`fomo-scarcity-${planKey}`}
          key={`scarcity-${spots}`}
        >
          <Zap className="h-3 w-3 text-amber-600" />
          Only <span className="font-extrabold tabular-nums">{spots}</span> spots left at this price
        </span>
      ) : (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-emerald-900 ring-1 ring-emerald-300 fomo-fade"
          data-testid={`fomo-social-${planKey}`}
          key={`social-${socialMessage}`}
        >
          <Flame className="h-3 w-3 text-rose-600" />
          {socialMessage}
        </span>
      )}
    </div>
  );
}
